import { useEffect } from "react";
import { useChecklistStore } from "../../Stores/Checklist";
import { useConfigStore } from "../../Stores/Config";
import { useHelpCenterStore } from "../../Stores/HelpCenter";
import { useNewsStore } from "../../Stores/News";
import { useStatusPageStore } from "../../Stores/StatusPage";
import { useTicketStore } from "../../Stores/Ticket";
import ChecklistCard from "../ChecklistCard/ChecklistCard";
import ConversationItem from "../ConversationItem/ConversationItem";
import CustomHomeWidgets from "../CustomHomeWidgets/CustomHomeWidgets";
import HelpCenterSearch from "../HelpCenterSearch/HelpCenterSearch";
import NewsCard from "../NewsCard/NewsCard";
import StatusCard from "../StatusCard/StatusCard";
import WidgetMenuButton from "../WidgetMenuButton/WidgetMenuButton";
// Own the card/list styles this renders (`.widget-app-container`,
// `.widget-menu > *` stagger, etc.) so it is styled wherever it is mounted —
// v3's WidgetMenu, v4's AgentHomeV4, and Storybook — not only when a sibling
// home version happens to pull WidgetMenu.scss into the bundle.
import "../WidgetMenu/WidgetMenu.scss";

// The shared home content list (news / help / status / conversations /
// announcement / checklist / custom widgets). Returns a fragment so it drops
// straight into whatever container the home version wraps it in — for v1-v3
// that is `.widget-menu` (below the version's own chatbar), for v4 its own
// content wrapper. The composer/feedback-pills are owned by each home version.
const WidgetContentList = () => {
  const configStore = useConfigStore();
  const ticketStore = useTicketStore();
  const newsStore = useNewsStore();
  const checklistStore = useChecklistStore();
  const helpCenterStore = useHelpCenterStore();
  const statusPageStore = useStatusPageStore();
  const latestConversation = ticketStore?.previousTickets?.[0];
  const unreadTickets = ticketStore.unreadTickets;
  const homeWidgets = configStore.homeWidgets;

  // Load every article that a news home card pins via config.articleId —
  // cards without a pin keep following newsStore.latestArticle.
  useEffect(() => {
    homeWidgets.forEach((widget) => {
      if (
        widget.type === "app" &&
        widget.config?.app === "LATEST_NEWS" &&
        widget.config?.articleId
      ) {
        useNewsStore.getState().loadPinnedArticle(widget.config.articleId);
      }
    });
  }, [homeWidgets]);

  const renderNews = (config: any, key: string) => {
    // Pinned to a specific article via config.articleId, otherwise the
    // latest one. An unavailable pin (draft, deleted, audience mismatch)
    // resolves to null and hides the card.
    const article = config?.articleId
      ? newsStore.pinnedArticles[config.articleId]
      : newsStore.latestArticle;
    if (!article) {
      return null;
    }
    return (
      <div className="widget-app-container" key={key}>
        <div className="widget-app-container-body">
          <NewsCard news={article} />
        </div>
      </div>
    );
  };

  const renderStatusPage = () => {
    if (!statusPageStore.statusPageName) {
      return null;
    }
    return (
      <div className="widget-app-container" key="status">
        <div className="widget-app-container-body">
          <StatusCard
            statusPageName={statusPageStore.statusPageName}
            statusPageUrl={statusPageStore.statusPageUrl}
            aggregateState={statusPageStore.aggregateState}
            resources={statusPageStore.resources}
          />
        </div>
      </div>
    );
  };

  const renderHelpCenterSearch = (label: string) => {
    return (
      <div className="widget-app-container" key="helpsearch">
        <div className="widget-app-container-body">
          <HelpCenterSearch
            label={label}
            articles={helpCenterStore.topArticles ?? []}
          />
        </div>
      </div>
    );
  };

  const renderAnnouncement = () => {
    const config = configStore.config;
    if (!config.showSpecialAnnouncement || !config.specialAnnouncement) {
      return null;
    }
    return (
      <div className="widget-app-container" key="announcement">
        <div className="widget-app-container-body">
          <div className="announcement-banner">
            {configStore.translateText(config.specialAnnouncement)}
          </div>
        </div>
      </div>
    );
  };

  const renderChecklist = () => {
    if (checklistStore.checklists?.length <= 0) {
      return null;
    }
    const latestChecklist = checklistStore.checklists[0];
    return (
      <div className="widget-app-container" key="checklist">
        <div className="widget-app-container-body">
          <ChecklistCard checklist={latestChecklist} key={latestChecklist.id} />
        </div>
      </div>
    );
  };

  return (
    <>
      {unreadTickets && unreadTickets.length > 0 ? (
        <div className="widget-unread-messages-container">
          {unreadTickets.slice(0, 2).map((ticket, index) => (
            <ConversationItem
              ticket={ticket}
              index={index}
              key={ticket.bugId}
            />
          ))}
        </div>
      ) : (
        latestConversation?.bugId &&
        configStore?.config?.showLatestConversation && (
          <div className="widget-unread-messages-container">
            <ConversationItem
              index={0}
              ticket={latestConversation}
              key={latestConversation.bugId}
            />
          </div>
        )
      )}
      {renderAnnouncement()}
      {renderChecklist()}
      {homeWidgets.map((widget, index) => {
        if (widget.type === "menu") {
          // v3 + v4 surface menu items as hero pills, not content buttons.
          // Must ask the store, not `config.v`: projects that never picked a
          // version have none, and reading it raw drew these buttons
          // underneath v4's pills, doubling every entry (#141887).
          if (configStore.getHomeVersion() >= 3) {
            return null;
          }
          return (
            <div className="widget-menu-button-container" key={index}>
              <div className="widget-menu-buttons">
                <WidgetMenuButton menuItem={widget.config} index={index} />
              </div>
            </div>
          );
        }

        if (widget.type === "app") {
          if (widget.config.app === "HELP_CENTER_SEARCH") {
            return renderHelpCenterSearch(widget.config.label);
          } else if (widget.config.app === "LATEST_NEWS") {
            return renderNews(widget.config, "news-" + index);
          } else if (widget.config.app === "INCIDENTS") {
            return renderStatusPage();
          }
        }

        return null;
      })}
      <CustomHomeWidgets />
    </>
  );
};

export default WidgetContentList;
